import type { QueryClient } from "@tanstack/react-query";
import type {
  LightStudyGroupResponse,
  PaginatedResponse,
} from "@/lib/humhub-api/types";
import { studyGroupKeys } from "@/lib/queryKeys/studyGroupKeys";

export const promoteStudyGroupInPage = (
  data: PaginatedResponse<LightStudyGroupResponse>,
  studyGroupId: number,
  shouldInsertAtTop: boolean,
  promotedGroup?: LightStudyGroupResponse | null,
) => {
  const resolvedPromotedGroup =
    promotedGroup ??
    data.results.find((group) => group.id === studyGroupId) ??
    null;

  if (!resolvedPromotedGroup) {
    return data;
  }

  const nextResults = data.results.filter((group) => group.id !== studyGroupId);

  return {
    ...data,
    results: shouldInsertAtTop
      ? [resolvedPromotedGroup, ...nextResults]
      : nextResults,
  };
};

export const promoteStudyGroupInMyStudyGroupsCache = (
  queryClient: QueryClient,
  studyGroupId: number,
) => {
  const matchingPages = queryClient.getQueriesData<
    PaginatedResponse<LightStudyGroupResponse>
  >({
    predicate: (query) => studyGroupKeys.isMyStudyGroupsPageKey(query.queryKey),
  });

  let promotedGroup: LightStudyGroupResponse | null = null;

  for (const [, data] of matchingPages) {
    const match = data?.results?.find((group) => group.id === studyGroupId);
    if (match) {
      promotedGroup = match;
      break;
    }
  }

  if (!promotedGroup) {
    return;
  }

  for (const [queryKey, data] of matchingPages) {
    if (!data?.results?.length) {
      continue;
    }

    const params = queryKey[1];
    const isFirstPage =
      typeof params === "object" &&
      params !== null &&
      "page" in params &&
      params.page === 1;

    queryClient.setQueryData<PaginatedResponse<LightStudyGroupResponse>>(
      queryKey,
      promoteStudyGroupInPage(data, studyGroupId, isFirstPage, promotedGroup),
    );
  }
};
